import chroma from 'chroma-js';
import { Box, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { IsoTileArea } from 'src/components/IsoTileArea/IsoTileArea';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useScene } from 'src/hooks/useScene';
import { getItemsAtTile, getTilePosition } from 'src/utils';

export const Cursor = () => {
  const theme = useTheme();
  const tile = useUiStateStore((state) => {
    return state.mouse.position.tile;
  });
  const zoom = useUiStateStore((state) => {
    return state.zoom;
  });
  const mode = useUiStateStore((state) => state.mode.type);
  const selectionCycle = useUiStateStore((state) => state.selectionCycle);
  const scene = useScene();
  const hoveredItems = useMemo(
    () => mode === 'CURSOR' ? getItemsAtTile({ tile, scene }) : [],
    [mode, tile, scene]
  );
  const activeCycle = selectionCycle &&
    selectionCycle.tile.x === tile.x &&
    selectionCycle.tile.y === tile.y
    ? selectionCycle
    : null;
  const hoveredItem = activeCycle?.item ?? hoveredItems[0] ?? null;
  const labelPosition = getTilePosition({ tile, origin: 'BOTTOM' });
  const feedbackColor = hoveredItem ? '#f59e0b' : theme.palette.primary.main;
  const hoveredItemLabel = hoveredItem
    ? ({
        ITEM: 'icon',
        TEXTBOX: 'text',
        CONNECTOR: 'connector',
        CONNECTOR_ANCHOR: 'connector point',
        RECTANGLE: 'rectangle'
      } as const)[hoveredItem.type]
    : undefined;

  return (
    <>
      <IsoTileArea
        from={tile}
        to={tile}
        fill={chroma(feedbackColor).alpha(hoveredItem ? 0.28 : 0.18).css()}
        cornerRadius={10 * zoom}
        stroke={{
          width: Math.max(1.5, 2 / zoom),
          color: chroma(feedbackColor).alpha(0.9).css(),
          dashArray: hoveredItem ? '0' : '6 4'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: labelPosition.x,
          top: labelPosition.y + 10,
          transform: 'translateX(-50%)',
          borderRadius: '6px',
          bgcolor: hoveredItem ? '#92400e' : 'rgba(15, 23, 42, 0.82)',
          color: '#fff',
          px: 0.75,
          py: 0.25,
          fontSize: 11,
          lineHeight: 1.4,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
        }}
      >
        {hoveredItem
          ? activeCycle && activeCycle.total > 1
            ? `${hoveredItemLabel} selected · ${activeCycle.index + 1} of ${activeCycle.total} · Click again to select next element.`
            : hoveredItems.length > 1
              ? `${hoveredItemLabel} · 1 of ${hoveredItems.length} · Click again to select next element.`
            : `${hoveredItemLabel} · click to select`
          : `tile ${tile.x}, ${tile.y}`}
      </Box>
    </>
  );
};
